import type { ManualRentalHistoryData } from 'services/rentalHistoryApi'

import { formValidationBuilder } from 'app/forms/utils/formValidationBuilder'
import type {
  ManualRentalHistoryEditData,
  RentalHistoryFormValues,
  SelectFieldOption,
  TenantFormValues,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import {
  COUNTRY_CODE_AUSTRALIA,
  getCountryCode,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/utils/addressUtils'
import {
  isAddressHistoryOwned,
  isAddressHistoryTemporaryBnBArrangement,
  isAddressHistoryWithFamilyOrFriends,
  isAgent,
  requireOtherTenantsAndLedger,
  validateAddress,
  // validateMinimumContactDetailProvided,
} from 'app/sm/helpers'
import { formatPayloadDate } from 'app/utils/datetime/helpers'

// Constants for joint tenancy options
export const JOINT_TENANCY_VALUES = {
  YES: 'yes',
  NO: 'no',
} as const

export const JOINT_TENANCY_LABELS = {
  YES: 'Yes',
  NO: 'No',
} as const

/**
 * Helper function to create a SelectFieldOption
 */
export const createSelectFieldOption = (
  value: string,
  label: string,
): SelectFieldOption => ({
  option: {
    value,
    label,
  },
  optionMeta: {
    id: value,
    value,
    displayText: label,
  },
})

/**
 * Helper function to determine if bond is paid based on bond value
 * @param bondValue - The bond field value (string or number)
 * @returns boolean indicating if bond should be considered as paid
 */
export const shouldBondBePaid = (bondValue: string | number): boolean => {
  if (!bondValue) return false
  const numericValue =
    typeof bondValue === 'string' ? parseFloat(bondValue) : bondValue
  return !!(numericValue && numericValue > 0)
}

/**
 * Helper function to extract property arrangement value for validation
 * Handles both SelectFieldOption format and direct numeric values
 */
export const getPropertyArrangementValue = (
  propertyArrangement: any,
): number => {
  if (!propertyArrangement) return 0

  // If it's already a number, return it
  if (typeof propertyArrangement === 'number') {
    return propertyArrangement
  }

  // Try to get value from option format
  const optionValue = getOptionValue(propertyArrangement)

  // Convert to number
  const numericValue =
    typeof optionValue === 'string' ? parseInt(optionValue, 10) : optionValue

  return isNaN(numericValue) ? 0 : numericValue
}

/**
 * Interface for application applicants data (from new_applicant-details.jsx)
 */
export interface ApplicationApplicant {
  guidID: string
  firstName: string
  lastName: string
  email: string
  mobileNumber: string
  isPrimary: boolean
}

/**
 * Map boolean isJointTenancy value to option type format
 * @param isJointTenancy - Boolean value from API
 * @returns SelectFieldOption for joint tenancy
 */
export const mapBooleanToJointTenancyOption = (
  isJointTenancy: boolean | SelectFieldOption,
): SelectFieldOption => {
  // If already in option format, return as-is
  if (typeof isJointTenancy === 'object' && isJointTenancy !== null) {
    return isJointTenancy
  }

  // Convert boolean to option format
  if (typeof isJointTenancy === 'boolean') {
    const value = isJointTenancy
      ? JOINT_TENANCY_VALUES.YES
      : JOINT_TENANCY_VALUES.NO
    const label = isJointTenancy
      ? JOINT_TENANCY_LABELS.YES
      : JOINT_TENANCY_LABELS.NO

    return createSelectFieldOption(value, label)
  }

  // Default to 'No' if value is undefined/null
  return createSelectFieldOption(
    JOINT_TENANCY_VALUES.NO,
    JOINT_TENANCY_LABELS.NO,
  )
}

/**
 * Helper function to check if joint tenancy is selected (value is 'yes')
 */
export const isJointTenancySelected = (
  isJointTenancy: boolean | SelectFieldOption,
): boolean => {
  if (typeof isJointTenancy === 'boolean') {
    return isJointTenancy
  }
  if (typeof isJointTenancy === 'object' && isJointTenancy?.optionMeta?.value) {
    return isJointTenancy.optionMeta.value === JOINT_TENANCY_VALUES.YES
  }
  return false
}

/**
 * Auto-fill tenant fields from application applicants
 * @param applicants - Array of application applicants
 * @param currentTenants - Current tenant form values
 * @returns Array of tenant form values with auto-filled data
 */
export const autoFillTenantsFromApplicants = (
  applicants: ApplicationApplicant[],
  currentTenants: TenantFormValues[],
): TenantFormValues[] => {
  // Filter out primary applicant
  const nonPrimaryApplicants = applicants.filter(
    (applicant) => !applicant.isPrimary,
  )

  // Only auto-fill if current tenant fields are empty or all fields are blank
  const shouldAutoFill =
    currentTenants.length === 0 ||
    currentTenants.every(
      (tenant) =>
        !tenant.firstName &&
        !tenant.lastName &&
        !tenant.email &&
        !tenant.mobileNumber,
    )

  if (shouldAutoFill && nonPrimaryApplicants.length > 0) {
    return nonPrimaryApplicants.map((applicant) => ({
      firstName: applicant.firstName || '',
      lastName: applicant.lastName || '',
      email: applicant.email || '',
      mobileNumber: applicant.mobileNumber || '',
      guid: applicant.guidID || '',
      relationship: '',
      isJointTenant: true,
    }))
  }

  return currentTenants
}

export const formFields = {
  address: 'address',
  // Individual address fields for proper validation
  'address.unitNumber': 'address.unitNumber',
  'address.streetNumber': 'address.streetNumber',
  'address.streetName': 'address.streetName',
  'address.suburb': 'address.suburb',
  'address.state': 'address.state',
  'address.postcode': 'address.postcode',
  'address.country': 'address.country',
  isCurrentAddress: 'isCurrentAddress',
  propertyArrangement: 'propertyArrangement',
  rate: 'rate',
  bond: 'bond',
  isBondPaid: 'isBondPaid',
  startDate: 'startDate',
  endDate: 'endDate',
  isRollingLease: 'isRollingLease',
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phoneNumber: 'phoneNumber',
  countryCode: 'countryCode',
  agencyName: 'agencyName',
  officeNumber: 'officeNumber',
  agentSearch: 'agentSearch',
  managerType: 'managerType',
  reasonForLeaving: 'reasonForLeaving',
  otherReason: 'otherReason',
  personalMessage: 'personalMessage',
  isJointTenancy: 'isJointTenancy',
  tenants: 'tenants',
  ledgerAttachments: 'ledgerAttachments',
  shouldContactReference: 'shouldContactReference',
  sendReferenceOnAdd: 'sendReferenceOnAdd',
}

// Helper function to extract value from option types
export const getOptionValue = (field: any): any => {
  if (typeof field === 'object' && field !== null) {
    return field.optionMeta?.value || field.option?.value || ''
  }
  return field
}

/**
 * Enhanced address validation that handles new CountryOption type
 * Converts the new format to legacy format and uses existing validation from helpers.js
 */
export const validateAddressWithOptions = (
  address: any,
): { ok: boolean; errorCode?: number } => {
  if (!address) {
    return { ok: false, errorCode: -1 }
  }

  // Convert new CountryOption format to legacy string format for validation
  const legacyAddress = {
    ...address,
    country: getCountryCode(address?.country) || address?.country || '',
    state: getOptionValue(address?.state) || '',
  }

  // Use existing validation logic from helpers.js
  return validateAddress(legacyAddress)
}

const fieldValidation = (
  fieldName: string,
  val: any,
  formData?: RentalHistoryFormValues,
) => {
  switch (fieldName) {
    case formFields.address: {
      const addressValidation = validateAddressWithOptions(val)

      // Check if address has all required fields
      if (!val || typeof val !== 'object') {
        return {
          required: 'Address is required',
        }
      }

      // Check individual required fields
      const missingFields = []
      if (!val.streetNumber || val.streetNumber.trim() === '') {
        missingFields.push('street number')
      }
      if (!val.streetName || val.streetName.trim() === '') {
        missingFields.push('street name')
      }
      if (!val.suburb || val.suburb.trim() === '') {
        missingFields.push('suburb')
      }
      if (!val.state || (!val.state.optionMeta?.value && !val.state.trim?.())) {
        missingFields.push('state')
      }
      // Postcode is optional based on the UI requirements

      const countryValue =
        getCountryCode(val.country) || val.country?.option?.value || val.country
      if (!countryValue) {
        missingFields.push('country')
      }

      // Return validation result
      if (missingFields.length > 0) {
        return {
          required: `Please complete: ${missingFields.join(', ')}`,
        }
      }

      if (!addressValidation.ok) {
        return {
          required: 'Please enter a valid address',
        }
      }

      return {}
    }
    // Individual address field validations for immediate blur feedback
    case formFields['address.streetNumber']: {
      return {
        required: (!val || val.trim() === '') && 'This field is required',
      }
    }
    case formFields['address.streetName']: {
      return {
        required: (!val || val.trim() === '') && 'This field is required',
      }
    }
    case formFields['address.suburb']: {
      return {
        required: (!val || val.trim() === '') && 'This field is required',
      }
    }
    case formFields['address.state']: {
      return {
        required:
          (!val || (!val.optionMeta?.value && !val.trim?.())) &&
          'This field is required',
      }
    }
    case formFields['address.postcode']: {
      return {
        required: (!val || val.trim() === '') && 'This field is required',
      }
    }
    case formFields['address.country']: {
      const countryValue = getCountryCode(val) || val?.option?.value || val
      return {
        required: !countryValue && 'This field is required',
      }
    }
    case formFields.propertyArrangement: {
      if (formData?.isQLDForm22) return {}
      return {
        required:
          (val === 0 || val === '' || !val?.optionMeta?.value) &&
          'This field is required',
      }
    }
    case formFields.rate: {
      if (formData?.isQLDForm22) return {}
      const ownedTheProperty = isAddressHistoryOwned(
        getOptionValue(formData?.propertyArrangement),
      )
      const livedWithFamilyOrFriends = isAddressHistoryWithFamilyOrFriends(
        getOptionValue(formData?.propertyArrangement),
      )
      const isTemporaryBnBArrangement = isAddressHistoryTemporaryBnBArrangement(
        getOptionValue(formData?.propertyArrangement),
      )
      const rentRequired =
        getOptionValue(formData?.propertyArrangement) !== 0 &&
        !ownedTheProperty &&
        !livedWithFamilyOrFriends &&
        !isTemporaryBnBArrangement
      return {
        required: rentRequired && val === '' && 'This field is required',
      }
    }
    case formFields.bond: {
      if (formData?.isQLDForm22) return {}
      return {
        required:
          formData?.isBondPaid && val === '' && 'This field is required',
      }
    }
    case formFields.firstName:
    case formFields.lastName: {
      const propertyArrangementValue =
        getPropertyArrangementValue(
          formData?.propertyArrangement,
        )?.toString() || ''
      const shouldShowRefereeSection = !shouldHideRefereeDetailsSection(
        propertyArrangementValue,
      )
      if (!shouldShowRefereeSection) {
        return {}
      }
      return {
        required: val === '' && 'This field is required',
      }
    }
    case formFields.email: {
      const propertyArrangementValue =
        getPropertyArrangementValue(
          formData?.propertyArrangement,
        )?.toString() || ''
      const shouldShowRefereeSection = !shouldHideRefereeDetailsSection(
        propertyArrangementValue,
      )
      if (!shouldShowRefereeSection) {
        return {}
      }
      return {
        required: val === '' && 'This field is required',
        format: val && !/\S+@\S+\.\S+/.test(val) && 'Invalid email format',
      }
    }
    case formFields.phoneNumber: {
      const propertyArrangementValue =
        getPropertyArrangementValue(
          formData?.propertyArrangement,
        )?.toString() || ''
      const shouldShowRefereeSection = !shouldHideRefereeDetailsSection(
        propertyArrangementValue,
      )
      if (!shouldShowRefereeSection) {
        return {}
      }
      // Contact validation is handled separately
      return {
        required: val === '' && 'This field is required',
      }
    }
    case formFields.officeNumber: {
      const propertyArrangementValue =
        getPropertyArrangementValue(
          formData?.propertyArrangement,
        )?.toString() || ''
      const shouldShowRefereeSection = !shouldHideRefereeDetailsSection(
        propertyArrangementValue,
      )
      if (!shouldShowRefereeSection) {
        return {}
      }
      // Contact validation is handled separately
      return {
        required: val === '' && 'This field is required',
      }
    }
    case formFields.agencyName: {
      const propertyArrangementValue =
        getPropertyArrangementValue(
          formData?.propertyArrangement,
        )?.toString() || ''
      const shouldShowRefereeSection = !shouldHideRefereeDetailsSection(
        propertyArrangementValue,
      )

      return {
        required:
          shouldShowRefereeSection &&
          isAgent(getOptionValue(formData?.managerType)) &&
          val === '' &&
          'This field is required',
      }
    }
    case formFields.tenants: {
      const propertyArrangementValue =
        getPropertyArrangementValue(
          formData?.propertyArrangement,
        )?.toString() || ''
      const shouldShowTenantsSection =
        shouldShowOtherTenantsAndLedger(propertyArrangementValue) &&
        !formData?.isQLDForm22

      // Only validate tenants if TenantsSection is shown AND joint tenancy is selected
      if (
        !shouldShowTenantsSection ||
        !formData?.isJointTenancy ||
        !isJointTenancySelected(formData.isJointTenancy)
      ) {
        return {}
      }

      const tenants = val || []
      const hasIncompleteTenant = tenants.some(
        (tenant: TenantFormValues) =>
          !tenant.firstName ||
          !tenant.lastName ||
          !tenant.email ||
          !tenant.mobileNumber,
      )
      return {
        required: tenants.length === 0 && 'At least one tenant is required',
        incomplete:
          hasIncompleteTenant && 'All tenant fields must be completed',
      }
    }
    case formFields.reasonForLeaving: {
      if (formData?.isQLDForm22) return {}
      // Reason for leaving is required when it's NOT the current address
      const isRequired = !formData?.isCurrentAddress
      const isEmpty = !val || !getOptionValue(val) || getOptionValue(val) === ''
      return {
        required: isRequired && isEmpty && 'This field is required',
      }
    }
    case formFields.startDate: {
      return {
        required: (!val || val === '') && 'This field is required',
      }
    }
    case formFields.endDate: {
      // End date is required unless it's current address or rolling lease
      const isRequired =
        !formData?.isCurrentAddress && !formData?.isRollingLease
      return {
        required:
          isRequired && (!val || val === '') && 'This field is required',
      }
    }
    case formFields.otherReason: {
      // Other reason is required when reason for leaving is "Other"
      const reasonValue = getOptionValue(formData?.reasonForLeaving)
      const isRequired = reasonValue === 'Other'
      return {
        required:
          isRequired && (!val || val.trim() === '') && 'This field is required',
      }
    }
    default:
      return {}
  }
}

/**
 * Form data transformation utilities
 */
export const transformFormData = (
  formData: RentalHistoryFormValues,
  isQLDForm22: boolean = false,
): RentalHistoryFormValues => {
  const arrangementValue = getPropertyArrangementValue(
    formData.propertyArrangement,
  )
  const ownedTheProperty = isAddressHistoryOwned(arrangementValue)
  const formattedBond = Number.isNaN(parseInt(formData.bond, 10))
    ? formData.bond
    : formData.bond
  const formattedRent = Number.isNaN(parseInt(formData.rate, 10))
    ? formData.rate
    : formData.rate

  if (!ownedTheProperty) {
    return {
      ...formData,
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName?.trim(),
      email: formData.email?.replace(/\s/g, ''),
      phoneNumber: formData.phoneNumber?.replace(/\s/g, ''),
      agencyName: isAgent(formData.managerType)
        ? formData.agencyName?.trim()
        : '',
      bond: formattedBond,
      rate: formattedRent,
      isBondPaid: shouldBondBePaid(formattedBond),
      startDate: formatPayloadDate(formData.startDate),
      endDate: formatPayloadDate(formData.endDate),
      officeNumber: isAgent(formData.managerType)
        ? formData.officeNumber.replace(/\s/g, '')
        : '',
      tenants: formData.tenants.map((tenant) => ({
        ...tenant,
        mobileNumber:
          tenant.mobileNumber && tenant.mobileNumber.replace(/\s/g, ''),
        email: tenant.email && tenant.email.replace(/\s/g, ''),
      })),
      isQLDForm22,
    }
  }

  return {
    ...formData,
    firstName: '',
    lastName: '',
    email: '',
    agencyName: '',
    countryCode: '',
    bond: '0',
    rate: '0',
    isBondPaid: false,
    startDate: formatPayloadDate(formData.startDate),
    endDate: formatPayloadDate(formData.endDate),
    personalMessage: '',
    tenants: formData.tenants.map((tenant) => ({
      ...tenant,
      mobileNumber:
        tenant.mobileNumber && tenant.mobileNumber.replace(/\s/g, ''),
      email: tenant.email && tenant.email.replace(/\s/g, ''),
    })),
    isQLDForm22,
  }
}

/**
 * Default form values
 */
export const getDefaultFormValues = (): RentalHistoryFormValues => ({
  address: {
    friendlyName: '',
    unitNumber: '',
    streetNumber: '',
    streetName: '',
    address2: '',
    suburb: '',
    state: '',
    postcode: '',
    country: {
      option: {
        value: COUNTRY_CODE_AUSTRALIA,
        label: 'Australia',
      },
      optionMeta: {
        id: COUNTRY_CODE_AUSTRALIA,
        displayText: 'Australia',
        value: COUNTRY_CODE_AUSTRALIA,
      },
    },
    lat: null,
    lng: null,
    googleId: '',
  },
  isCurrentAddress: false,
  propertyArrangement: {
    option: {
      value: 0,
      label: '',
    },
    optionMeta: {
      id: 0,
      displayText: '',
      value: 0,
    },
  },
  rate: '',
  bond: '',
  isBondPaid: false,
  startDate: '',
  endDate: '',
  isRollingLease: false,
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  countryCode: '',
  agencyName: '',
  officeNumber: '',
  agentSearch: '',
  managerType: {
    option: {
      value: 1,
      label: 'Agent',
    },
    optionMeta: {
      id: 1,
      displayText: 'Agent',
      value: 1,
    },
  },
  reasonForLeaving: {
    option: {
      value: '',
      label: '',
    },
    optionMeta: {
      id: '',
      displayText: '',
      value: '',
    },
  },
  otherReason: '',
  personalMessage: '',
  isJointTenancy: createSelectFieldOption(
    JOINT_TENANCY_VALUES.NO,
    JOINT_TENANCY_LABELS.NO,
  ),
  tenants: [],
  ledgerAttachments: [],
  shouldContactReference: true,
  sendReferenceOnAdd: false,
})

/**
 * Build API request payload from form values
 */
export const buildRequestPayload = (
  formValues: RentalHistoryFormValues,
  isQLDForm22: boolean = false,
) => {
  const transformedData = transformFormData(formValues, isQLDForm22)

  // Transform form data for API
  const {
    address,
    propertyArrangement,
    managerType,
    reasonForLeaving,
    ...restData
  } = transformedData
  const { lat, lng, country, state, ...restAddress } = address

  return {
    ...restData,
    address: {
      ...restAddress,
      // Extract state value from option type
      state: getOptionValue(state) || '',
      // Convert CountryOption back to string for API and add as countryCode
      country: getOptionValue(country),
      // Only include lat/lng if they exist
      ...(lat && lng ? { lat, lng } : {}),
    },
    // For QLD Form 22, always send ArrangementNotDisclosedRenting (11)
    propertyArrangement: isQLDForm22
      ? ARRANGEMENT_NOT_DISCLOSED_RENTING
      : getOptionValue(propertyArrangement) || 0,
    // Convert ManagerType option to value for API
    managerType: getOptionValue(managerType) || 1,
    reasonForLeaving: getOptionValue(reasonForLeaving) || '',
  }
}

/**
 * Build manual rental history API request payload from form values
 */
export const buildManualRentalHistoryPayload = (
  formValues: RentalHistoryFormValues,
  applicantGuid: string,
  agencyGuid: string,
  applicantDetails: {
    firstName: string
    lastName: string
    email: string
    mobileNumber: string
  },
  isQLDForm22: boolean = false,
): ManualRentalHistoryData => {
  return {
    applicant_guid: applicantGuid,
    agency_guid: agencyGuid,
    applicant_details: applicantDetails,
    rental_history_request: {
      firstName: formValues.firstName || '', // formFields.firstName
      lastName: formValues.lastName || '', // formFields.lastName
      email: formValues.email || '', // formFields.email
      phoneNumber: formValues.phoneNumber || '', // formFields.phoneNumber
      officeNumber: formValues.officeNumber || '', // formFields.officeNumber
      address: {
        unitNumber: formValues.address?.unitNumber || '', // formFields.address
        streetNumber: formValues.address?.streetNumber || '', // formFields.address
        streetName: formValues.address?.streetName || '', // formFields.address
        suburb: formValues.address?.suburb || '', // formFields.address
        state: getOptionValue(formValues.address?.state) || '', // formFields.address
        postcode: formValues.address?.postcode || '', // formFields.address
        country: getCountryCode(formValues.address?.country) || 'AU', // formFields.address
      },
      propertyType: (formValues as any).propertyType || 2, // formFields.propertyType
      bedrooms: (formValues as any).bedrooms || '', // formFields.bedrooms
      rate: parseFloat(formValues.rate) || 0, // formFields.rate
      bond: parseFloat(formValues.bond) || 0, // formFields.bond
      isRollingLease: formValues.isRollingLease || false, // formFields.isRollingLease
      startDate: formatPayloadDate(formValues.startDate) || '', // formFields.startDate
      endDate: formatPayloadDate(formValues.endDate) || '', // formFields.endDate
      managerType: getOptionValue(formValues.managerType) || 1, // formFields.managerType
      agencyName: formValues.agencyName || '', // formFields.agencyName
      reasonForLeaving: getOptionValue(formValues.reasonForLeaving) || '', // formFields.reasonForLeaving
      isCurrentAddress: formValues.isCurrentAddress || false, // formFields.isCurrentAddress
      propertyArrangement: isQLDForm22
        ? ARRANGEMENT_NOT_DISCLOSED_RENTING
        : getOptionValue(formValues.propertyArrangement) || 1, // formFields.propertyArrangement
      personalMessage: formValues.personalMessage || '', // formFields.personalMessage
      isBondPaid: shouldBondBePaid(formValues.bond), // formFields.isBondPaid - automatically set based on bond value
      shouldContactReference: formValues.shouldContactReference || false, // formFields.shouldContactReference
      sendReferenceOnAdd: formValues.sendReferenceOnAdd || false, // formFields.sendReferenceOnAdd
      tenants: formValues.tenants || [], // formFields.tenants
      LedgersIDs: (formValues.ledgerAttachments || []).map(
        (l: any) => l.guidID,
      ),
      isQLDForm22: isQLDForm22,
    },
  }
}

/**
 * Transform API errors to field-level errors
 */
export const transformApiErrorToFields = (apiErrors: any) => {
  // This will depend on your API error structure
  // For now, return a basic transformation
  return Object.keys(apiErrors).reduce((acc, key) => {
    acc[key] = Array.isArray(apiErrors[key])
      ? apiErrors[key][0]
      : apiErrors[key]
    return acc
  }, {} as Record<string, string>)
}

/**
 * Alias for backward compatibility
 */
export const getInitialFormValues = getDefaultFormValues

/**
 * Transform manual rental history edit data to form initial values
 */
export const transformManualRentalHistoryToFormValues = (
  editData: ManualRentalHistoryEditData,
): Partial<RentalHistoryFormValues> => {
  const defaultValues = getDefaultFormValues()

  const transformedValues = {
    ...defaultValues,
    // Address mapping
    address: {
      ...defaultValues.address,
      unitNumber: editData.address?.unitNumber || '',
      streetNumber: editData.address?.streetNumber || '',
      streetName: editData.address?.streetName || '',
      suburb: editData.address?.suburb || '',
      state: editData.address?.state
        ? {
            option: {
              value: editData.address.state,
              label: getStateLabel(editData.address.state),
            },
            optionMeta: {
              id: editData.address.state,
              displayText: getStateLabel(editData.address.state),
              value: editData.address.state,
            },
          }
        : '',
      postcode: editData.address?.postcode || '',
      country: editData.address?.country
        ? {
            option: {
              value: editData.address.country,
              label: getCountryLabel(editData.address.country),
            },
            optionMeta: {
              id: editData.address.country,
              displayText: getCountryLabel(editData.address.country),
              value: editData.address.country,
            },
          }
        : defaultValues.address.country,
    },
    // Basic fields
    startDate: editData.startDate || '',
    endDate: editData.endDate || '',
    rate: editData.rate?.toString() || '',
    bond: editData.bond?.toString() || '',
    firstName: editData.firstName || '',
    lastName: editData.lastName || '',
    email: editData.email || '',
    phoneNumber: editData.phoneNumber || '',
    officeNumber: editData.officeNumber || '',
    agencyName: editData.agencyName || '',
    reasonForLeaving: editData.reasonForLeaving
      ? {
          option: {
            value: editData.reasonForLeaving,
            label: editData.reasonForLeaving,
          },
          optionMeta: {
            id: editData.reasonForLeaving.toLowerCase().replace(/\s+/g, '_'),
            displayText: editData.reasonForLeaving,
            value: editData.reasonForLeaving,
          },
        }
      : defaultValues.reasonForLeaving,
    personalMessage: editData.personalMessage || '',
    isBondPaid: editData.isBondPaid || false,
    isCurrentAddress: editData.isCurrentAddress || false,
    // Set isRollingLease based on editData or derive from missing endDate
    isRollingLease:
      editData.isRollingLease ||
      (!!editData.startDate && !editData.endDate && !editData.isCurrentAddress),
    shouldContactReference: editData.shouldContactReference ?? true,
    sendReferenceOnAdd: editData.sendReferenceOnAdd ?? false,
    // Property arrangement (convert number to option format)
    propertyArrangement: editData.propertyArrangement
      ? {
          option: {
            value: editData.propertyArrangement,
            label: getPropertyArrangementLabel(editData.propertyArrangement),
          },
          optionMeta: {
            id: editData.propertyArrangement,
            displayText: getPropertyArrangementLabel(
              editData.propertyArrangement,
            ),
            value: editData.propertyArrangement,
          },
        }
      : defaultValues.propertyArrangement,
    // Manager type (convert number to option format, default to Agent if not provided)
    managerType: editData.managerType
      ? {
          option: {
            value: editData.managerType,
            label: getManagerTypeLabel(editData.managerType),
          },
          optionMeta: {
            id: editData.managerType,
            displayText: getManagerTypeLabel(editData.managerType),
            value: editData.managerType,
          },
        }
      : defaultValues.managerType,
    // Tenants (filter not primary tenants)
    tenants:
      editData.tenants
        ?.filter((tenant) => !tenant.isPrimary)
        ?.map((tenant) => ({
          firstName: tenant.firstName || '',
          lastName: tenant.lastName || '',
          email: tenant.email || '',
          mobileNumber: tenant.mobileNumber || '',
          guid: tenant.guid || '',
          relationship: tenant.relationship || '',
          isJointTenant: true,
        })) || [],
    // Map isJointTenancy boolean to option format
    isJointTenancy: mapBooleanToJointTenancyOption(
      editData.isJointTenancy ??
        (editData.tenants?.filter((tenant) => !tenant.isPrimary)?.length || 0) >
          0,
    ),
    // Ledger attachments (handle both 'ledger' and 'ledgerAttachments' fields)
    ledgerAttachments: editData.ledger || editData.ledgerAttachments || [],
    // System field for edit mode
    id: editData.guidID,
  }

  return transformedValues
}

// Helper functions for option labels
const getPropertyArrangementLabel = (value: number): string => {
  const arrangements: Record<number, string> = {
    8: 'I rented with an agent',
    9: 'I rented with private owner',
    6: 'I sub-letted',
    2: 'I boarded',
    7: 'I lived with family/friends',
    5: 'I owned the property',
    10: 'Temporary / BnB',
    // Legacy deprecated options (for backward compatibility)
    1: 'I rented',
    3: 'Lived with Friends',
    4: 'Lived with Family',
  }
  return arrangements[value] || 'Unknown'
}

const getManagerTypeLabel = (value: number): string => {
  const types: Record<number, string> = {
    1: 'Agent',
    2: 'Owner',
  }
  return types[value] || 'Unknown'
}

const getCountryLabel = (countryCode: string): string => {
  const countries: Record<string, string> = {
    AU: 'Australia',
    AUS: 'Australia',
    US: 'United States',
    USA: 'United States',
    GB: 'United Kingdom',
    UK: 'United Kingdom',
    CA: 'Canada',
    NZ: 'New Zealand',
    // Add more countries as needed
  }
  return countries[countryCode] || countryCode
}

const getStateLabel = (stateCode: string): string => {
  const states: Record<string, string> = {
    NSW: 'NSW',
    VIC: 'VIC',
    QLD: 'QLD',
    SA: 'SA',
    WA: 'WA',
    TAS: 'TAS',
    NT: 'NT',
    ACT: 'ACT',
    // For Australian states, display the same as the code
  }
  return states[stateCode] || stateCode
}

export { fieldValidation }
export const formValidation = formValidationBuilder(fieldValidation)

/**
 * Utility function to filter manual rental history tenants to only include primary tenants
 * This function ensures consistent filtering across the application
 */
export const filterManualRentalHistoryTenants = (
  manualRentalHistory: any[],
) => {
  if (!manualRentalHistory || !Array.isArray(manualRentalHistory)) {
    return []
  }

  return manualRentalHistory.map((entry) => ({
    ...entry,
    tenants: entry.tenants
      ? entry.tenants.filter((tenant: any) => tenant.isPrimary === true)
      : [],
  }))
}

/**
 * Determines if referee details section should be hidden
 */
export const shouldHideRefereeDetailsSection = (propertyArrangement: any) => {
  const arrangementValue = getPropertyArrangementValue(propertyArrangement)
  const ownedTheProperty = isAddressHistoryOwned(arrangementValue)
  return (
    ownedTheProperty ||
    isAddressHistoryTemporaryBnBArrangement(arrangementValue)
  )
}

/**
 * Determines if other tenants and ledger section should be shown
 */
export const shouldShowOtherTenantsAndLedger = (
  propertyArrangement: string,
) => {
  return requireOtherTenantsAndLedger(propertyArrangement)
}

/**
 * Determines if rental ledger section should be hidden
 */
export const shouldHideRentalLedger = (propertyArrangement: string) => {
  const dontShowRefereeDetails =
    shouldHideRefereeDetailsSection(propertyArrangement)
  const livedWithFamilyOrFriends =
    isAddressHistoryWithFamilyOrFriends(propertyArrangement)
  return dontShowRefereeDetails || livedWithFamilyOrFriends
}

/**
 * Constant for QLD Form 22 - Arrangement Not Disclosed (Renting)
 * This is used to indicate the property arrangement when it's not disclosed
 */
export const ARRANGEMENT_NOT_DISCLOSED_RENTING = 11
